import get from "lodash.get";
import isEmpty from "lodash.isempty";
import { useEffect, useMemo, useState } from "react";

import * as Client from "api/client";
import { Annotation, PAGE_KEYS } from "config/const";
import Loading from "components/Loading/Loading";
import { useAuth } from "providers/Auth";
import { DELIVERY_FEE_PATH } from "utils/shop-settings";
import Context from "./Context";

const pagesActions = Object.values(PAGE_KEYS).filter(
  (v) => v !== PAGE_KEYS.MESSAGE
);

const getAnnotationFns = (annotations) => {
  const findAnnotation = (key = "") =>
    annotations.find((annotation) => annotation.key === key) || {};
  const findDynamicItems = () =>
    annotations.filter(
      (annotation) =>
        !annotation.isSystem && annotation.type !== Annotation.Type.ITEM_LIST
    ) || [];
  const findItemList = () =>
    annotations.filter(
      (annotation) =>
        !annotation.isSystem && annotation.type === Annotation.Type.ITEM_LIST
    ) || [];
  const findAnnotations = (keys = []) => keys.map((key) => findAnnotation(key));
  const findPrerequisities = (key, value, isSystem = false) => {
    if (value === undefined || value === null) return [];
    return (
      annotations.filter((annotation) => {
        if (
          !annotation.prerequisities ||
          Boolean(annotation.isSystem) !== isSystem
        )
          return false;
        if (Array.isArray(annotation.prerequisities))
          return annotation.prerequisities.find((pre) => {
            const [[k, v]] = Object.entries(pre);
            return k === key && v === value;
          });
        return annotation.prerequisities[key] === value;
      }) || []
    );
  };

  return {
    findAnnotation,
    findDynamicItems,
    findItemList,
    findAnnotations,
    findPrerequisities,
  };
};

const Provider = ({ children, mock }) => {
  const [shop, setShop] = useState(isEmpty(mock) ? {} : mock);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [productTags, setProductTags] = useState([]);
  const [pages, setPages] = useState([]);

  const [fetched, setFetched] = useState(false);

  const { auth } = useAuth();

  const getShop = () =>
    Client.getShop(auth.accessToken, auth.userId).then((res) => {
      if (!res.err) {
        //TODO: i18n
        if (res && res.annotations) {
          Array.from(res.annotations).forEach((e) => {
            if (e.key == "orderer") {
              e.label = "User";
            } else if (e.key == "payment") {
              e.label = "Payment";
              console.log("Payment.sources");
              if (e.sources) {
                Array.from(e.sources).forEach((s) => {
                  if (s.key == "FACE") {
                    s.label = "CASH";
                  }
                });
              }
            } else if (e.key == "delivery") {
              e.label = "Delivery";
              if (e.sources) {
                Array.from(e.sources).forEach((s) => {
                  if (s.key == "HOME") {
                    s.label = "Home Delivery";
                  }
                });
              }
            } else if (e.key == "address") {
              e.label = "Address";
            }
          });
        }

        setShop({
          ...res,
          shopSettings: res.settings,
        });
        return true;
      } else {
        return false;
      }
    });

  const getProducts = () =>
    Client.getProducts(auth.idToken).then((res) => {
      if (!res.err) {
        setProducts(res.filter(({ status }) => status !== "OFF"));
        return true;
      } else {
        return false;
      }
    });

  const getPages = () =>
    Client.getPages(auth.idToken, pagesActions).then((res) => {
      if (!res.err) {
        setPages(res);
        return true;
      } else {
        return false;
      }
    });

  const getCategories = () =>
    Client.getCategories(auth.idToken).then((res) => {
      if (!res.err) {
        setCategories(res);
        return true;
      } else {
        return false;
      }
    });

  const getCollections = () =>
    Client.getCollections(auth.idToken).then((res) => {
      if (!res.err) {
        setCollections(res);
        return true;
      } else {
        return false;
      }
    });

  const getProductTags = () =>
    Client.getTags(auth.idToken).then((res) => {
      if (!res.err) {
        const productTags = res.filter(({ target }) => target === "PRODUCT");
        setProductTags(productTags);
        return true;
      } else {
        return false;
      }
    });

  const getShopLandingPage = () =>
    Client.getLandingPage(auth.idToken).then((res) => {
      if (!res.err) {
        setShop((prevShop) => ({
          ...prevShop,
          landingPage: res,
        }));
        return true;
      } else {
        return false;
      }
    });

  const getCollectionProducts = (tagId) =>
    Client.getCollectionProducts(auth.idToken, tagId).then((res) => {
      if (!res.err) {
        return res;
      } else {
        return false;
      }
    });

  useEffect(() => {
    if (!auth.idToken || !auth.idToken.token || fetched) return;

    Promise.all([
      getProducts(),
      getCategories(),
      getCollections(),
      getProductTags(),
      getPages(),
      getShop(),
      getShopLandingPage(),
    ])
      .then(([pState, cState, ptState, pageState, sState]) => {
        setFetched(pState && cState && ptState && pageState && sState);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [auth]);

  const annotations = get(shop, "annotations", []);
  const {
    findAnnotation,
    findDynamicItems,
    findItemList,
    findAnnotations,
    findPrerequisities,
  } = useMemo(() => getAnnotationFns(annotations), [annotations]);

  const props = {
    shop: shop || {},
    products,
    categories,
    collections,
    productTags,
    pages,
    hasShop: isEmpty(shop),
    deliveryFee: get(shop, DELIVERY_FEE_PATH, {}),
    branches: get(shop, "branches", []) || [],
    annotations,
    fetched,
    getCollectionProducts,
    setShop,
    findAnnotation,
    findAnnotations,
    findPrerequisities,
    findDynamicItems,
    findItemList,
  };

  return (
    <Context.Provider value={{ ...props }}>
      {fetched ? children : <Loading />}
    </Context.Provider>
  );
};

export default Provider;
